import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import apiClient from '@/lib/api-client';

export interface PreviousStrategyAllocation {
  accountName: string;
  accountSourceCode: string | null;
  calculationMethod: string | null;
  rate: number | null;
  rateType: string | null;
  percentage: number;
}

export interface PreviousFirelightPrefill {
  dataItems: Array<{
    DataItemId: string;
    Value: string;
  }>;
  skippedFields: Array<{
    valueKey: string;
    dataItemId: string;
    reason: string;
  }>;
  mappingStatus: string | null;
}

export interface PreviousRecommendedProduct {
  id: string;
  position: number | null;
  originalPosition: number | null;
  productId: string | null;
  carrierName: string | null;
  productName: string | null;
  productType: string | null;
  amBestRating: string | null;
  recommendationScore: number | null;
  surrenderPeriodYears: number | null;
  firelightCusip: string | null;
  firelightCarrierCode: string | null;
  firelightPrefill: PreviousFirelightPrefill;
  strategyAllocations: PreviousStrategyAllocation[];
  originalStrategyAllocations: PreviousStrategyAllocation[];
}

export interface PreviousRecommendations {
  id: string;
  forwardedAt: string;
  forwardedBy: string | null;
  recommendationSummary: string | null;
  products: PreviousRecommendedProduct[];
}

interface PreviousRecommendationsResponse {
  forward: PreviousRecommendations | null;
}

export const previousRecommendationsKeys = {
  all: ['previous-recommendations'] as const,
  detail: (opportunityId: string) => [...previousRecommendationsKeys.all, opportunityId] as const,
};

async function fetchPreviousRecommendations(
  opportunityId: string
): Promise<PreviousRecommendations | null> {
  const response = await apiClient.get<PreviousRecommendationsResponse>(
    `/opportunities/${opportunityId}/previous-recommendations`
  );
  return response.data.forward ?? null;
}

export function usePreviousRecommendations(
  opportunityId: string,
  options?: Omit<UseQueryOptions<PreviousRecommendations | null>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: previousRecommendationsKeys.detail(opportunityId),
    queryFn: () => fetchPreviousRecommendations(opportunityId),
    enabled: !!opportunityId,
    staleTime: 60 * 1000,
    ...options,
  });
}
